import React from "react";
import intl from "react-intl-universal";

import {TableDataSource} from "../dataSources/TableDataSource";
import {FilterSchema} from "../dataSources/DataSource";
import {ColId, ColSchema, Row} from "../../ui/components/Table";
import {GQLResponse} from "../gqlModel/types";
import {BasicDataSourceDeps} from "../../platform/services/types/DataSourceDependencies";
import {queryGqlApi} from "../../graphql/queryGqlApi";
import {listAuditAssignments} from "../../graphql/audit/queries";
import {
    onCreateAuditAssignment,
    onUpdateAuditAssignment,
    onDeleteAuditAssignment,
} from "../../graphql/audit/subscriptions";
import {InTextLabel} from "../../ui/components/InTextLabel/InTextLabel";
import {Button} from "../../ui/components/Button/Button";
import {getMuiIcon} from "../../ui/components/icons/createMuiIcon";
import {AuditAssignment} from "./types";

const ENDPOINT = "pgServer";

const colOrder: ColId[] = ['audit', 'location', 'assignee', 'status', 'dueAt', 'actions'];

const colSchema: ColSchema = {
    audit: {
        getTitle: () => intl.get('audits.assignments.colAudit').d('Audit'),
        alignLeft: true,
        minWidth: 220,
    },
    location: {
        getTitle: () => intl.get('audits.assignments.colLocation').d('Location'),
        alignLeft: true,
        minWidth: 160,
    },
    assignee: {
        getTitle: () => intl.get('audits.assignments.colAssignee').d('Assignee'),
        alignLeft: true,
        minWidth: 160,
    },
    status: {
        getTitle: () => intl.get('audits.assignments.colStatus').d('Status'),
        minWidth: 130,
        maxWidth: 170,
    },
    dueAt: {
        getTitle: () => intl.get('audits.assignments.colDue').d('Due'),
        minWidth: 120,
        maxWidth: 160,
    },
    actions: {
        getTitle: () => '',
        minWidth: 150,
        maxWidth: 190,
    },
};

const statusLabel = (status: AuditAssignment['status']) => {
    switch (status) {
        case 'completed':
            return <InTextLabel type="success" size="sm" text={intl.get('audits.assignments.completed').d('Completed')}/>;
        case 'in_progress':
            return <InTextLabel type="info" size="sm" text={intl.get('audits.assignments.inProgress').d('In progress')}/>;
        case 'cancelled':
            return <InTextLabel type="neutral" size="sm" text={intl.get('audits.assignments.cancelled').d('Cancelled')}/>;
        case 'assigned':
        default:
            return <InTextLabel type="warning" size="sm" text={intl.get('audits.assignments.assigned').d('Assigned')}/>;
    }
};

export class AuditAssignmentDS extends TableDataSource<AuditAssignment> {
    readonly _modelName = 'AuditAssignment' as const;
    readonly wsId: string;

    readonly _subscriptionGQLs = {
        onCreate: onCreateAuditAssignment,
        onUpdate: onUpdateAuditAssignment,
        onDelete: onDeleteAuditAssignment,
    };

    getAuditName?: (id: string) => string | undefined;
    getLocationName?: (id?: string | null) => string | undefined;
    getUserName?: (id: string) => string | undefined;
    onStartClick?: (assignment: AuditAssignment) => void;
    onEditClick?: (assignment: AuditAssignment) => void;
    showStartAction = true;

    constructor(deps: BasicDataSourceDeps, wsId: string) {
        super(deps);
        this.wsId = wsId;
        this.setSortRule('dueAt');
    }

    get syncHash() { return this.wsId; }
    get syncQueryFilter() { return {workspaceId: this.wsId}; }

    getFilterSchema(): FilterSchema<AuditAssignment> {
        return {
            audit: {
                type: 'client',
                apply: (assignment) => {
                    const needle = (this.filterValues.audit ?? '').toLowerCase();
                    const audit = this.getAuditName?.(assignment.auditTemplateId) ?? assignment.auditTemplateId;
                    const location = this.getLocationName?.(assignment.locationId) ?? '';
                    const user = this.getUserName?.(assignment.assignedUserId) ?? '';
                    return `${audit} ${location} ${user}`.toLowerCase().includes(needle);
                },
            },
            status: {
                type: 'client',
                apply: (assignment) => {
                    const value = this.filterValues.status;
                    if (!value || value === 'all') return true;
                    if (value === 'active') return assignment.status === 'assigned' || assignment.status === 'in_progress';
                    return assignment.status === value;
                },
            },
        };
    }

    getFilterDefaultValues() { return {audit: '', status: 'all'}; }

    loadGQLFetch(): Promise<GQLResponse<AuditAssignment>> {
        return queryGqlApi(
            listAuditAssignments, {workspaceId: this.wsId},
            this.deps.dataClientService, this.deps.authService, ENDPOINT,
        ) as Promise<GQLResponse<AuditAssignment>>;
    }

    loadMoreGQLFetch(nextToken: string): Promise<GQLResponse<AuditAssignment>> {
        return queryGqlApi(
            listAuditAssignments, {workspaceId: this.wsId, nextToken},
            this.deps.dataClientService, this.deps.authService, ENDPOINT,
        ) as Promise<GQLResponse<AuditAssignment>>;
    }

    getWsClient() {
        return this.deps.dataClientService.getWsClientByKey(ENDPOINT);
    }

    getColsSchema(): ColSchema { return colSchema; }
    getColOrder(): ColId[] { return colOrder; }

    mapResultToTableRow = (assignment: AuditAssignment): Row => {
        const canStart = assignment.status === 'assigned' || assignment.status === 'in_progress';
        return {
            ...assignment,
            audit: this.getAuditName?.(assignment.auditTemplateId) ?? assignment.auditTemplateId,
            location: this.getLocationName?.(assignment.locationId) ?? '—',
            assignee: this.getUserName?.(assignment.assignedUserId) ?? assignment.assignedUserId,
            status: statusLabel(assignment.status),
            dueAt: assignment.dueAt ? new Date(assignment.dueAt).toLocaleDateString() : '—',
            actions: (
                <div className="Audits-rowActions" onClick={(event) => event.stopPropagation()}>
                    {this.onEditClick && (
                        <Button
                            type="neutral"
                            style="plain"
                            size="sm"
                            Icon={getMuiIcon("edit")}
                            text={intl.get('common.edit').d('Edit')}
                            onClick={() => this.onEditClick?.(assignment)}
                        />
                    )}
                    {this.showStartAction && (
                        <Button
                            type="primary"
                            style="soft"
                            size="sm"
                            Icon={getMuiIcon("play_arrow")}
                            text={assignment.status === 'in_progress'
                                ? intl.get('audits.assignments.continue').d('Continue')
                                : intl.get('audits.assignments.start').d('Start')}
                            isDisable={!canStart}
                            onClick={() => this.onStartClick?.(assignment)}
                        />
                    )}
                </div>
            ),
            raw: assignment,
        };
    };

    get activeAssignments(): AuditAssignment[] {
        return this.allRows.filter((assignment) => assignment.status === 'assigned' || assignment.status === 'in_progress');
    }

    upsertAuditAssignment(assignment: AuditAssignment) {
        this._upsertItem(assignment, true);
    }

    removeAuditAssignment(id: string) {
        this._removeItem(id);
    }
}
